import { useMemo, useState } from 'react';

import { apiFetch } from '@/api/client';
import { bookingsKey } from '@/api/bookings';
import { depositsKey } from '@/api/deposits';
import {
  approvalQueueKey,
  inspectionsKey,
  pendingInspectionsKey,
  useApprovalQueue,
  type PendingApproval,
  type ReturnInspection,
} from '@/api/inspections';
import { useApiMutation } from '@/api/mutations';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { formatAmount } from '@core/format/money';
import type { Session } from '@core/session/types';
import { ApprovalDrawer } from './approvals/ApprovalDrawer';
import { summarise, uncovered, waitTone, waitedFor } from './approvals/queue';

/**
 * Section 9's "approval required above a configurable threshold" — the manager's side of it.
 *
 * <p>
 * <strong>The queue is the control.</strong> A charge over the threshold is recorded and
 * deliberately not billed, so until somebody signs for it the deposit behind it settles as though
 * the garment came back clean. That makes this list the one place where a customer is being kept
 * waiting by the shop's own paperwork rather than by anything they did — which is why it is a
 * screen of its own rather than a filter on the returns queue, and why the longest wait leads.
 * </p>
 *
 * <p>
 * This page owns the query and the mutation and nothing else; `ApprovalDrawer` is presentational
 * and takes a row plus an intent, exactly as the booking counters do.
 * </p>
 */
export function ApprovalsPage({ session }: { session: Session }) {
  const [reviewing, setReviewing] = useState<PendingApproval | null>(null);

  const queue = useApprovalQueue();

  const rows = useMemo(() => queue.data?.rows ?? [], [queue.data]);
  const summary = useMemo(() => summarise(rows), [rows]);

  const decide = useApiMutation<ReturnInspection, { approve: boolean; note: string | null }>({
    mutationFn: (body) =>
      apiFetch<ReturnInspection>(
        `/api/return-inspection/garment/${reviewing?.rentalOrderItemId}/approve`,
        { method: 'POST', body },
      ),
    success: (_, input) =>
      input.approve ? 'Approved. The charge is on the booking.' : 'Refused. The charge stays off.',
    /*
      Approving is the moment `RentalOrderItem.DamageCharge` is finally written, so three other
      screens are now wrong: the booking carries a charge it did not, the returns queue shows it as
      applied rather than assessed, and the deposit register's exposure has moved.
    */
    invalidates: [
      approvalQueueKey,
      inspectionsKey,
      pendingInspectionsKey,
      bookingsKey,
      depositsKey,
    ],
    onSuccess: () => setReviewing(null),
  });

  const columns: DataViewColumn<PendingApproval>[] = [
    {
      title: 'Garment',
      dataIndex: 'garmentName',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.garmentName}</div>
          <div className="font-mono-nums text-[13px] text-default">
            {row.serialCode}
            {row.bookingNumber ? ` · ${row.bookingNumber}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      render: (_, row) => (
        <div>
          <div className="text-sm text-gray-900">{row.customerName}</div>
          {row.wearerName && row.wearerName !== row.customerName && (
            <div className="text-[13px] text-default">worn by {row.wearerName}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Charge',
      dataIndex: 'proposedCharge',
      align: 'right',
      render: (_, row) => (
        <div>
          <div className="font-mono-nums text-[13px] font-semibold text-gray-900">
            {formatAmount(row.proposedCharge)}
          </div>
          {row.replacementValueUsed && <div className="text-[13px] text-danger">written off</div>}
        </div>
      ),
    },
    {
      title: 'Beyond the deposit',
      dataIndex: 'depositHeld',
      align: 'right',
      render: (_, row) => {
        const gap = uncovered(row);

        return (
          <span className={`font-mono-nums text-[13px] ${gap === null ? '' : 'text-danger'}`}>
            {/* A dash means covered, which is the ordinary case and not worth a figure. What is
                worth one is the part the shop would be chasing rather than deducting. */}
            {gap === null ? '—' : formatAmount(gap)}
          </span>
        );
      },
    },
    {
      title: 'Agreed at the counter',
      dataIndex: 'acknowledgedByCustomer',
      hideOnCard: true,
      render: (_, row) =>
        row.acknowledgedByCustomer ? (
          <StatusBadge tone="positive">Agreed</StatusBadge>
        ) : (
          <StatusBadge tone="warning">Not agreed</StatusBadge>
        ),
    },
    {
      title: 'Waiting',
      dataIndex: 'daysWaiting',
      render: (_, row) => (
        <StatusBadge tone={waitTone(row.daysWaiting)}>{waitedFor(row.daysWaiting)}</StatusBadge>
      ),
    },
    {
      title: '',
      dataIndex: 'rentalOrderItemId',
      trailing: true,
      /*
        Reviewing is a read and stays open to anyone who may see the screen; only the decision is
        gated. The button opens the breakdown rather than deciding, which is the point: the
        threshold stopped this charge so that somebody would look at what it is made of.
      */
      render: (_, row) => (
        <WriteAction session={session} required="ReturnInspection:Approve">
          {(disabled) => (
            <Button
              icon="icon icon-badge-check"
              disabled={disabled}
              onClick={() => setReviewing(row)}
            >
              Review
            </Button>
          )}
        </WriteAction>
      ),
    },
  ];

  const noThreshold = (queue.data?.threshold ?? 0) <= 0;

  return (
    <>
      <PageHeading
        title="Charge approvals"
        subtitle="Damage charges large enough to need a signature. Until one is given the money is not on the booking, and the deposit behind it settles as though nothing was found."
      />

      {summary.count > 0 && (
        <div className="mb-3 grid gap-3 sm:grid-cols-3 lg:mb-6">
          <Card>
            <div className="text-[13px] text-default">Held back</div>
            <div className="font-mono-nums mt-1 text-xl font-semibold text-gray-900">
              {formatAmount(summary.heldBack)}
            </div>
            {/* Said plainly because it is the opposite of every other money tile in the product:
                this is not owed to the shop, it is money the shop has not asked for. */}
            <div className="mt-0.5 text-[13px] text-default">
              recorded, and on nobody&rsquo;s bill
            </div>
          </Card>

          <Card>
            <div className="text-[13px] text-default">Waiting</div>
            <div className="font-mono-nums mt-1 text-xl font-semibold text-gray-900">
              {summary.count}
            </div>
            <div className="mt-0.5 text-[13px] text-default">
              {waitedFor(summary.longestWaitDays).toLowerCase()}
            </div>
          </Card>

          <Card>
            <div className="text-[13px] text-default">Not agreed at the counter</div>
            <div
              className={`font-mono-nums mt-1 text-xl font-semibold ${
                summary.unacknowledged > 0 ? 'text-danger' : 'text-gray-900'
              }`}
            >
              {summary.unacknowledged}
            </div>
            <div className="mt-0.5 text-[13px] text-default">the ones that come back</div>
          </Card>
        </div>
      )}

      {/*
        An empty queue and no threshold look identical, and they are not the same news. A shop that
        has set none is not clear — it has no control at all, and every damage charge bills itself.
      */}
      {noThreshold && (
        <div className="mb-3 lg:mb-6">
          <Notice tone="info" title="No approval threshold is set">
            Every damage charge bills itself, however large, so nothing can appear here. Set a
            figure under Booking defaults on the Settings screen to hold the big ones back for a
            signature.
          </Notice>
        </div>
      )}

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <DataView<PendingApproval>
          columns={columns}
          dataSource={rows}
          loading={queue.isLoading}
          rowKey="rentalOrderItemId"
          empty={
            <EmptyState
              icon="icon icon-badge-check"
              title="Nothing waiting on a signature"
              hint={
                noThreshold
                  ? 'With no threshold set, damage charges apply as soon as an inspection is saved.'
                  : `Charges over ${formatAmount(queue.data?.threshold ?? 0)} appear here until somebody signs for them.`
              }
            />
          }
        />
      </Card>

      {reviewing && (
        <ApprovalDrawer
          row={reviewing}
          saving={decide.isPending}
          onDecide={(payload) => decide.mutate(payload)}
          onClose={() => setReviewing(null)}
        />
      )}
    </>
  );
}
